import React, { useEffect, useRef, useState } from 'react';
import { waLink } from '../lib/maabarContact';
import './HelpButton.css';

// «تحتاج مساعدة؟» (owner, 2026-11-02): ONE floating button, site-wide, in place
// of AIHub's «AI» button and the WhatsApp button. It opens both: the MAABAR
// assistant tools (AIHub's panels, opened through the `maabar:open-tool`
// event) and WhatsApp.
// It never covers a sticky bar or a primary button: it rises above any fixed
// bar at the bottom of the screen (the product page's sticky bar, the bottom
// navigation), and fades out while it would sit on a primary button or on the
// page title.
// On a phone it opens as the icon alone and shows its words once the visitor
// scrolls (owner, 2026-11-02): on load it must not cover the product title.

const T = {
  ar: { help: 'تحتاج مساعدة؟', wa: 'تواصل معنا على واتساب', waSub: 'فريق معبر يرد عليك', msg: 'مرحبًا، أحتاج مساعدة من فريق مَعبر.', close: 'إغلاق',
    tools: [['assistant', 'مساعد معبر الذكي', 'يفهم فكرتك ويرتّب طلبك'], ['calc', 'حاسبة التكلفة', 'تكلفة · شحن · ربح']] },
  en: { help: 'Need help?', wa: 'Chat with us on WhatsApp', waSub: 'The MAABAR team replies', msg: 'Hello, I need help from the Maabar team.', close: 'Close',
    tools: [['assistant', 'MAABAR assistant', 'Understands your idea and shapes your request'], ['calc', 'Cost calculator', 'Cost · shipping · profit']] },
};
T.zh = T.en;

// Fixed bars at the bottom of the screen the button must stay above, and the buttons it must never cover.
const BARS = '[data-sticky-bar], [data-bottom-nav], .db-bottom-nav';
const PRIMARY = '[data-sticky-add], [data-add-cart], [data-cart-next], [data-primary-cta], .mk-btn:not(.ghost), .mk-ship-card, h1';
const PHONE = '(max-width: 760px)';
const SCROLLED = 120;   // px: the words appear after this much scrolling

const visible = (el) => { const r = el.getBoundingClientRect(); const s = getComputedStyle(el); return s.display !== 'none' && s.visibility !== 'hidden' && r.height > 0 && r.width > 0 ? r : null; };
const overlap = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

export default function HelpButton({ lang = 'ar', showTools = true }) {
  const c = T[lang] || T.ar;
  const [open, setOpen] = useState(false);
  const [lift, setLift] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(PHONE).matches);
  const box = useRef(null);
  const btn = useRef(null);

  // stay above fixed bottom bars; fade while on a primary button
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      let top = window.innerHeight;
      document.querySelectorAll(BARS).forEach((el) => { const r = visible(el); if (r && r.bottom >= window.innerHeight - 2) top = Math.min(top, r.top); });
      const nextLift = Math.max(0, window.innerHeight - top);
      setLift((x) => (x === nextLift ? x : nextLift));
      const small = !!window.matchMedia?.(PHONE).matches && window.scrollY < SCROLLED;
      setCompact((x) => (x === small ? x : small));
      const me = btn.current && btn.current.getBoundingClientRect();
      if (!me) return;
      const hit = [...document.querySelectorAll(PRIMARY)].some((el) => !box.current?.contains(el) && !el.closest(BARS) && (() => { const r = visible(el); return r && overlap(me, r); })());
      setHidden((x) => (x === hit ? x : hit));
    };
    const ask = () => { if (!raf) raf = requestAnimationFrame(check); };
    check();
    window.addEventListener('scroll', ask, { passive: true });
    window.addEventListener('resize', ask);
    const mo = new MutationObserver(ask);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    return () => { window.removeEventListener('scroll', ask); window.removeEventListener('resize', ask); mo.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const out = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', out); window.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); window.removeEventListener('keydown', esc); };
  }, [open]);

  const tool = (id) => { setOpen(false); window.dispatchEvent(new CustomEvent('maabar:open-tool', { detail: id })); };
  return (
    <div ref={box} className={`help-fab${hidden && !open ? ' hide' : ''}${compact && !open ? ' compact' : ''}`} data-help-button="1" dir={lang === 'ar' ? 'rtl' : 'ltr'}
      style={{ bottom: `calc(${lift + 14}px + env(safe-area-inset-bottom, 0px))` }}>
      {open && (
        <div className="help-menu" role="menu" data-help-menu="1">
          {showTools && c.tools.map(([id, title, sub]) => (
            <button key={id} type="button" role="menuitem" data-help-tool={id} onClick={() => tool(id)}><b>{title}</b><span>{sub}</span></button>
          ))}
          <a role="menuitem" href={waLink(c.msg)} target="_blank" rel="noopener noreferrer" data-help-wa="1" onClick={() => setOpen(false)}>
            <b>{c.wa}</b><span>{c.waSub}</span>
          </a>
        </div>
      )}
      <button ref={btn} type="button" className="help-fab-btn" aria-expanded={open} aria-haspopup="menu" aria-label={open ? c.close : c.help} onClick={() => setOpen((x) => !x)} data-help-toggle="1">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" /><path d="M10 9.2a2 2 0 1 1 2.6 1.9c-.4.2-.6.5-.6.9" /><path d="M12 13.6h.01" /></svg>
        <span>{open ? c.close : c.help}</span>
      </button>
    </div>
  );
}
